import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Bell, CheckCheck, CheckCircle2, Info, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { tempoDecorrido } from '@/lib/format';
import { supabase } from '@/lib/supabase';
// Realtime de notificações já é feito globalmente pelo useNotificacoesRealtime
// no app-shell.tsx (com toast em INSERT). Não duplicar aqui.

interface Notificacao {
  seq_notificacao: number;
  cod_tipo: string;
  cod_severidade: 'INFO' | 'SUCESSO' | 'AVISO' | 'ERRO';
  des_titulo: string;
  des_corpo: string | null;
  des_url_acao: string | null;
  ind_lida: boolean;
  dta_criacao: string;
}

const ICONS = {
  INFO: Info,
  SUCESSO: CheckCircle2,
  AVISO: AlertTriangle,
  ERRO: XCircle,
} as const;

// Gradientes premium por severidade — vibe Apple Wallet (AVISO sem amber chamativo)
const GRADIENTES = {
  INFO: 'from-sky-400 via-blue-500 to-indigo-600',
  SUCESSO: 'from-emerald-400 via-teal-500 to-cyan-600',
  // Aviso vira coral-laranja-pêssego (warm porém menos "atenção urgente" do amber puro)
  AVISO: 'from-orange-400 via-orange-500 to-rose-500',
  ERRO: 'from-rose-500 via-red-600 to-rose-800',
} as const;

/** Agrupa notificações em buckets "Hoje", "Ontem", "Esta semana", "Mais antigas". */
function agrupar(notifs: Notificacao[]): { bucket: string; items: Notificacao[] }[] {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const ontem = new Date(hoje);
  ontem.setDate(ontem.getDate() - 1);
  const seteDias = new Date(hoje);
  seteDias.setDate(seteDias.getDate() - 7);

  const buckets = new Map<string, Notificacao[]>([
    ['Hoje', []],
    ['Ontem', []],
    ['Esta semana', []],
    ['Mais antigas', []],
  ]);

  for (const n of notifs) {
    const d = new Date(n.dta_criacao);
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    if (day.getTime() === hoje.getTime()) buckets.get('Hoje')!.push(n);
    else if (day.getTime() === ontem.getTime()) buckets.get('Ontem')!.push(n);
    else if (day.getTime() >= seteDias.getTime()) buckets.get('Esta semana')!.push(n);
    else buckets.get('Mais antigas')!.push(n);
  }

  return Array.from(buckets.entries())
    .filter(([, items]) => items.length > 0)
    .map(([bucket, items]) => ({ bucket, items }));
}

export function NotificacoesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const notif = useQuery({
    queryKey: ['notificacoes', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_notificacoes_in_app')
        .select(
          'seq_notificacao, cod_tipo, cod_severidade, des_titulo, des_corpo, des_url_acao, ind_lida, dta_criacao',
        )
        .eq('cod_auth_user', user!.id)
        .order('dta_criacao', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Notificacao[];
    },
    enabled: !!user,
  });

  const marcarLida = useMutation({
    mutationFn: async (seq: number) => {
      const { error } = await supabase
        .from('tab_notificacoes_in_app')
        .update({ ind_lida: true, dta_leitura: new Date().toISOString() })
        .eq('seq_notificacao', seq);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
    },
  });

  const marcarTodasLidas = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('tab_notificacoes_in_app')
        .update({ ind_lida: true, dta_leitura: new Date().toISOString() })
        .eq('cod_auth_user', user!.id)
        .eq('ind_lida', false);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
    },
  });

  // Marca como lida automaticamente quando entra na tela (após 2s).
  const marcarTodasLidasRef = useRef(marcarTodasLidas);
  marcarTodasLidasRef.current = marcarTodasLidas;

  useEffect(() => {
    if (notif.data && notif.data.some((n) => !n.ind_lida)) {
      const t = setTimeout(() => marcarTodasLidasRef.current.mutate(), 2000);
      return () => clearTimeout(t);
    }
  }, [notif.data]);

  const naoLidas = notif.data?.filter((n) => !n.ind_lida).length ?? 0;
  const grupos = useMemo(() => (notif.data ? agrupar(notif.data) : []), [notif.data]);

  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Page header */}
      <motion.div
        className="octa-page-header flex items-end justify-between"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div>
          <h1>Notificações</h1>
          <p>
            {naoLidas > 0
              ? `${naoLidas} não ${naoLidas === 1 ? 'lida' : 'lidas'} · atualizadas em tempo real`
              : 'Tudo em dia por aqui.'}
          </p>
        </div>
        {naoLidas > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => marcarTodasLidas.mutate()}
            className="h-9 shrink-0 px-3 text-xs font-semibold"
          >
            <CheckCheck className="mr-1 h-3.5 w-3.5" />
            Marcar todas como lidas
          </Button>
        )}
      </motion.div>

      {notif.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-20 rounded-2xl" />
        </div>
      ) : notif.data?.length === 0 ? (
        <EmptyState
          variant="hero"
          icon={Bell}
          title="Sem notificações por aqui"
          description="Você sera avisado aqui em primeira mão sobre lavagens, pagamentos e novidades."
        />
      ) : (
        <div className="space-y-6">
          {grupos.map((grupo, gi) => (
            <motion.section
              key={grupo.bucket}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + gi * 0.05, duration: 0.45 }}
              className="space-y-2"
            >
              <div className="flex items-center gap-3">
                <h2 className="octa-section-title mb-0">{grupo.bucket}</h2>
                <div className="octa-divider flex-1" aria-hidden />
              </div>
              <ul className="space-y-2">
                <AnimatePresence initial={false}>
                  {grupo.items.map((n, i) => {
                    const Icon = ICONS[n.cod_severidade];
                    const grad = GRADIENTES[n.cod_severidade];
                    const conteudo = (
                      <Card
                        variant="default"
                        className={`octa-card-tap relative overflow-hidden ${
                          n.ind_lida ? 'opacity-75' : ''
                        }`}
                      >
                        {/* Faixa colorida à esquerda quando não lida */}
                        {!n.ind_lida && (
                          <div
                            className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b ${grad}`}
                            aria-hidden
                          />
                        )}
                        <CardContent className="flex items-start gap-3 p-4">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${grad} shadow-octa-sm text-white`}
                          >
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={`text-sm leading-snug ${
                                  n.ind_lida ? 'font-medium' : 'font-bold'
                                }`}
                              >
                                {n.des_titulo}
                              </p>
                              <span className="text-muted-foreground shrink-0 whitespace-nowrap text-[11px] font-medium">
                                {tempoDecorrido(n.dta_criacao)}
                              </span>
                            </div>
                            {n.des_corpo && (
                              <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                                {n.des_corpo}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );

                    return (
                      <motion.li
                        key={n.seq_notificacao}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: i * 0.04, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      >
                        {n.des_url_acao ? (
                          <Link
                            to={n.des_url_acao}
                            onClick={() => !n.ind_lida && marcarLida.mutate(n.seq_notificacao)}
                            className="block"
                          >
                            {conteudo}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => !n.ind_lida && marcarLida.mutate(n.seq_notificacao)}
                            className="block w-full text-left"
                          >
                            {conteudo}
                          </button>
                        )}
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
              </ul>
            </motion.section>
          ))}
        </div>
      )}
    </div>
  );
}
